import { useCallback, useEffect, useRef, useState } from "react";
import { buildEntryRedirectUrl } from "../utils/buildLandingRedirect";
import {
  runAntiBotChecks,
  validateSliderGesture,
  isDevToolsOpen,
  forceCloseOnDevTools,
  getPreservedSearchQuery,
} from "../utils/gateAntiBot";
import { getSensitiveWord } from "../utils/sensitiveWords";
import "./SliderGate.css";

const STEPS = [
  { id: "ps1", dot: "1", labelKey: "初始化" },
  { id: "ps2", dot: "2", labelKey: "加载资源" },
  { id: "ps3", dot: "3", labelKey: "准备就绪" },
  { id: "ps4", dot: "✓", labelKey: "完成" },
];

const STEP_THRESHOLDS = [1, 30, 60, 95];
const PASS_REDIRECT_MS = 0x2bc;

function stepClass(progress, i) {
  const at = STEP_THRESHOLDS[i];
  if (progress >= at) return "done";
  if (i === 0 || progress >= STEP_THRESHOLDS[i - 1]) return "active";
  return "";
}

export default function SliderGate() {
  const [progress, setProgress] = useState(0);
  const [verifyVisible, setVerifyVisible] = useState(false);
  const [dragPercent, setDragPercent] = useState(0);
  const [thumbOffset, setThumbOffset] = useState(0);
  const [passed, setPassed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [statusText, setStatusText] = useState("");

  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const fillRef = useRef(null);
  const passedRef = useRef(false);
  const progressTimerRef = useRef(null);
  const maxOffsetRef = useRef(0);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const dragStartMsRef = useRef(0);
  const dragTrackRef = useRef([]);
  const redirectTimerRef = useRef(null);

  const measure = useCallback(() => {
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!track || !thumb) return;
    const tw = thumb.offsetWidth || 0x2c;
    maxOffsetRef.current = Math.max(track.clientWidth - tw - 8, 0);
    if (maxOffsetRef.current <= 0) {
      maxOffsetRef.current = Math.max(track.clientWidth - 0x34, 0);
    }
  }, []);

  const clientX = (e) => {
    if (e.touches?.length) return e.touches[0].clientX;
    if (e.changedTouches?.length) return e.changedTouches[0].clientX;
    return e.clientX;
  };

  const finishPass = useCallback(
    (track) => {
      const anti = runAntiBotChecks(track);

      passedRef.current = true;
      setPassed(true);
      setDragPercent(100);
      setThumbOffset(maxOffsetRef.current);
      setStatusText(getSensitiveWord("验证通过，正在进入..."));

      redirectTimerRef.current = window.setTimeout(() => {
        window.location.replace(
          buildEntryRedirectUrl({
            antiBot: {
              hitModules: anti.hitModules,
              totalScore: anti.totalScore,
            },
            extraQuery: getPreservedSearchQuery(),
          })
        );
      }, PASS_REDIRECT_MS);
    },
    []
  );

  const onMove = useCallback(
    (e) => {
      if (!draggingRef.current) return;
      const dx = Math.max(
        0,
        Math.min(clientX(e) - startXRef.current, maxOffsetRef.current)
      );
      dragTrackRef.current.push({
        x: dx,
        t: Date.now() - dragStartMsRef.current,
      });
      setThumbOffset(dx);
      const pct =
        maxOffsetRef.current > 0 ? (dx / maxOffsetRef.current) * 100 : 0;
      setDragPercent(pct);
      if (fillRef.current) fillRef.current.style.width = `${pct}%`;
      e.preventDefault();
    },
    []
  );

  const onEnd = useCallback(
    (e) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setDragging(false);
      thumbRef.current?.classList.remove("dragging");

      const dx = Math.max(
        0,
        Math.min(clientX(e) - startXRef.current, maxOffsetRef.current)
      );
      const durationMs = Date.now() - dragStartMsRef.current;
      const gesture = validateSliderGesture(
        dragTrackRef.current,
        maxOffsetRef.current,
        dx,
        durationMs
      );

      if (gesture.ok) {
        if (isDevToolsOpen()) {
          forceCloseOnDevTools();
          return;
        }
        finishPass(dragTrackRef.current);
        return;
      }

      setThumbOffset(0);
      setDragPercent(0);
      dragTrackRef.current = [];
      if (fillRef.current) fillRef.current.style.width = "0%";

      const reached =
        maxOffsetRef.current > 0 && dx >= maxOffsetRef.current * 0.9;
      setStatusText(
        reached
          ? getSensitiveWord("滑动速度过快，请重试")
          : getSensitiveWord("请滑动到最右侧")
      );
      window.setTimeout(() => {
        if (!passedRef.current) setStatusText("");
      }, 0x708);
    },
    [finishPass]
  );

  const onStart = useCallback(
    (e) => {
      if (passedRef.current) return;
      if (isDevToolsOpen()) {
        forceCloseOnDevTools();
        return;
      }
      measure();
      draggingRef.current = true;
      setDragging(true);
      dragStartMsRef.current = Date.now();
      startXRef.current = clientX(e);
      dragTrackRef.current = [];
      thumbRef.current?.classList.add("dragging");
      e.preventDefault();
    },
    [measure]
  );

  useEffect(() => {
    const stages = [
      { target: 0x1c, delay: 0x8c },
      { target: 0x3a, delay: 0xc8 },
      { target: 0x52, delay: 0xa0 },
      { target: 0x64, delay: 0xdc },
    ];
    let i = 0;
    const tick = () => {
      if (i >= stages.length) {
        setProgress(100);
        progressTimerRef.current = window.setTimeout(() => {
          setVerifyVisible(true);
        }, 0x15e);
        return;
      }
      const s = stages[i++];
      setProgress(s.target);
      progressTimerRef.current = window.setTimeout(tick, s.delay);
    };
    tick();

    return () => {
      if (progressTimerRef.current) window.clearTimeout(progressTimerRef.current);
      if (redirectTimerRef.current) window.clearTimeout(redirectTimerRef.current);
    };
  }, []);

  /** 验证区出现后再绑 thumb；挂载时 verifyVisible 仍为 false，thumb 尚未渲染 */
  useEffect(() => {
    if (!verifyVisible) return;

    measure();
    const thumb = thumbRef.current;
    if (thumb) {
      thumb.addEventListener("mousedown", onStart, { passive: false });
      thumb.addEventListener("touchstart", onStart, { passive: false });
    }
    document.addEventListener("mousemove", onMove, { passive: false });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("mouseup", onEnd);
    document.addEventListener("touchend", onEnd);
    window.addEventListener("resize", measure);

    return () => {
      if (thumb) {
        thumb.removeEventListener("mousedown", onStart);
        thumb.removeEventListener("touchstart", onStart);
      }
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("mouseup", onEnd);
      document.removeEventListener("touchend", onEnd);
      window.removeEventListener("resize", measure);
    };
  }, [verifyVisible, measure, onStart, onMove, onEnd]);

  const sslActive = dragPercent >= 10 || passed;
  const shieldActive = dragPercent >= 0x2b || passed;
  const privacyActive = dragPercent >= 0x4c || passed;

  return (
    <div className="gate-page">
      <div className="ambient" aria-hidden="true">
        <div className="ambient-orb orb-1" />
        <div className="ambient-orb orb-2" />
        <div className="ambient-orb orb-3" />
      </div>

      <div className="page-wrap">
        <div className="card" role="main">
          <div className="card-header">
            <h1 className="card-title">{getSensitiveWord("安全验证")}</h1>
            <p className="card-sub">
              {getSensitiveWord("为保障访问安全，请完成以下验证")}
            </p>
          </div>

          <div className="divider" role="separator" />

          {!verifyVisible && (
            <div
              className="pg-wrap"
              aria-live="polite"
              aria-label={getSensitiveWord("正在加载")}
            >
              <div className="pg-header">
                <span className="pg-label">
                  {getSensitiveWord("正在加载，请稍候...")}
                </span>
                <span className="pg-pct">{progress}%</span>
              </div>
              <div className="pg-track" role="progressbar" aria-valuenow={progress}>
                <div className="pg-bar" style={{ width: `${progress}%` }} />
              </div>
              <div className="pg-steps" aria-hidden="true">
                {STEPS.map((step, i) => (
                  <div key={step.id} className={`pg-step ${stepClass(progress, i)}`}>
                    <div className="step-dot">{step.dot}</div>
                    <span className="step-label">
                      {getSensitiveWord(step.labelKey)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="pg-dots" aria-hidden="true">
                <span className="pg-dot" />
                <span className="pg-dot" />
                <span className="pg-dot" />
              </div>
            </div>
          )}

          {verifyVisible && (
            <div
              className="verify-wrap visible"
              aria-label={getSensitiveWord("人机验证")}
            >
              <div className="badges" aria-hidden="true">
                <div className={`badge ${sslActive ? "badge-active" : ""}`} id="badge-ssl">
                  <div className="badge-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </div>
                  <span className="badge-text">
                    {getSensitiveWord("SSL 加密")}
                  </span>
                </div>
                <div className={`badge ${shieldActive ? "badge-active" : ""}`} id="badge-shield">
                  <div className="badge-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                  </div>
                  <span className="badge-text">
                    {getSensitiveWord("安全防护")}
                  </span>
                </div>
                <div className={`badge ${privacyActive ? "badge-active" : ""}`} id="badge-privacy">
                  <div className="badge-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 8v4l3 3" />
                    </svg>
                  </div>
                  <span className="badge-text">
                    {getSensitiveWord("隐私保护")}
                  </span>
                </div>
              </div>

              <div className="verify-hint">
                <p>
                  {getSensitiveWord("请")}
                  <strong>{getSensitiveWord("向右滑动")}</strong>
                  {getSensitiveWord("完成人机验证")}
                </p>
              </div>

              <div
                className={`slider-container ${passed ? "passed" : ""}`}
                role="slider"
                aria-valuenow={Math.round(dragPercent)}
              >
                <div ref={trackRef} className="slider-track">
                  <div
                    ref={fillRef}
                    className="slider-fill"
                    style={{ width: `${dragPercent}%` }}
                  />
                  <div className="slider-text">
                    {getSensitiveWord("向右滑动验证")}
                  </div>
                  <div className="arrow-hints" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                  </div>
                </div>
                <div
                  ref={thumbRef}
                  className={`slider-thumb ${dragging ? "dragging" : ""} ${passed ? "done" : ""}`}
                  style={{ transform: `translateX(${thumbOffset}px)` }}
                  aria-hidden="true"
                >
                  {passed ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    "›"
                  )}
                </div>
              </div>
              <div className="slider-status" role="status" aria-live="polite">
                {passed
                  ? getSensitiveWord("验证通过，正在进入...")
                  : statusText}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
